import type { PhieuChiDetailRow, PhieuChiFormInitialData, PhieuChiTaxRow, PurchaseInvoiceSource } from '../types';
import { resolvePaymentDocumentInvoiceId } from '@/lib/payment-document-utils';
import { transformDocumentNumber } from '@/components/custom/erp';
import type { PhieuChiInput } from '@/types/schemas';
import { isValidUUID } from '@/lib/uuid-validator';
import { FormFieldState } from '../hooks';

type LookupRecord = Record<string, unknown> & { uuid?: string };
type InvoiceDetailSource = Record<string, unknown> & {
  tk_dt_data?: LookupRecord | null;
  ma_dv_data?: Record<string, unknown> | null;
  thue_suat_data?: (LookupRecord & { thue_suat?: string | number }) | null;
  ma_thue_data?: (LookupRecord & { thue_suat?: string | number }) | null;
  ma_bp_data?: LookupRecord | null;
  ma_vv_data?: LookupRecord | null;
  ma_hd_data?: LookupRecord | null;
  ma_dtt_data?: LookupRecord | null;
  ma_ku_data?: LookupRecord | null;
  ma_phi_data?: LookupRecord | null;
  ma_cp0_data?: LookupRecord | null;
  tien_nt?: number;
  tien?: number;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const lookupUuid = (value: unknown): string | null => {
  const record = asRecord(value);
  return typeof record?.uuid === 'string' ? record.uuid : null;
};

const lookupString = (value: unknown, key: string): string | null => {
  const record = asRecord(value);
  const fieldValue = record?.[key];
  return typeof fieldValue === 'string' ? fieldValue : null;
};

const scalarValue = (value: unknown): string | number | undefined =>
  typeof value === 'string' || typeof value === 'number' ? value : undefined;

const getNestedLookup = (value: unknown, key: string): LookupRecord | null => {
  const nested = asRecord(value)?.[key];
  return asRecord(nested) as LookupRecord | null;
};

const getInvoiceDetail = (hoaDonData: PurchaseInvoiceSource, index = 0): InvoiceDetailSource =>
  (hoaDonData.chi_tiet_hoa_don?.[index] || hoaDonData.chi_tiet_data?.[index] || {}) as InvoiceDetailSource;

/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (
  detailRows: PhieuChiDetailRow[],
  formMode: string,
  dien_giai: string,
  ma_ngv: string
) => {
  return detailRows.map((row, index: number) => {
    const uuid = formMode !== 'add' && isValidUUID(row.uuid) ? { uuid: row.uuid } : {};
    return {
      line: index + 1,
      ...uuid,

      // Description and basic info
      dien_giai: row.dien_giai || dien_giai || null,
      ghi_chu: row.ghi_chu || null,

      // Customer information
      ma_kh: row.ma_kh_data?.uuid || row.ma_kh || null,

      // Account information
      tk_no: row.tk_no_data?.uuid || row.id_hd_data?.tk_data?.uuid || row.tk_no || null,
      tknh2: row.tknh2_data?.uuid || null,

      // Document references
      so_ct0: row.so_ct0_data || row.so_ct0 || null,
      so_ct2: row.so_ct2_data || row.so_ct2 || null,
      ngay_ct0: row.ngay_ct0 || null,

      // Financial amounts
      ty_gia2: row.ty_gia2 || 0,
      tien_nt: row.tien_nt || 0,
      tien: row.tien || 0,

      // Tax information
      ma_thue: lookupUuid(row.ma_thue_data) || null,
      thue_suat: row.thue_suat || 0,
      tk_thue: row.tk_thue || null,
      thue_nt: row.thue_nt || 0,
      thue: row.thue_nt || 0,

      // Invoice and contract information
      ma_loai_hd: ma_ngv != '1' && ma_ngv != '4' ? row.ma_loai_hd : null,
      id_hd: resolvePaymentDocumentInvoiceId(ma_ngv, row.id_hd_data?.ID, null),
      ma_mau_ct: row.ma_mau_ct || null,
      ma_mau_bc: row.ma_mau_bc || null,
      ma_tc_thue: lookupUuid(row.ma_tc_thue_data) || null,
      ma_kh_thue: lookupUuid(row.ma_kh_thue_data) || null,
      dia_chi: lookupString(row.ma_kh_thue_data, 'address') || row.dia_chi || null,
      ma_so_thue: lookupString(row.ma_kh_thue_data, 'tax_code') || row.ma_so_thue || null,
      ten_vt_thue: row.ten_vt_thue || null,

      // Additional customer info
      ma_kh9: lookupUuid(row.ma_kh9_data) || null,

      // Request and equipment tracking
      id_dn: row.line_dn || 0,
      line_dn: row.line_dn || 0,
      id_tb: row.id_tb || 0,
      line_tb: row.line_tb || 0,
      id_tt: row.id_tt || 0,

      // Department and organizational units
      ma_bp: row.ma_bp_data?.uuid || row.ma_bp || null,
      ma_vv: row.ma_vv_data?.uuid || row.ma_vv || null,
      ma_hd: row.ma_hd_data?.uuid || row.ma_hd || null,
      ma_dtt: row.ma_dtt_data?.uuid || row.ma_dtt || null,
      ma_ku: row.ma_ku_data?.uuid || row.ma_ku || null,
      ma_phi: row.ma_phi_data?.uuid || row.ma_phi || null,
      ma_sp: row.ma_sp_data?.uuid || row.ma_sp || null,
      ma_lsx: row.ma_lsx || null,
      ma_cp0: row.ma_cp0_data?.uuid || row.ma_cp0 || null
    };
  });
};

export const transformTaxRows = (taxRows: PhieuChiTaxRow[], formMode: string) => {
  return taxRows.map((row, index: number) => {
    const uuid = formMode !== 'add' && isValidUUID(row.uuid) ? { uuid: row.uuid } : {};

    return {
      ...uuid,
      line: index + 1,
      so_ct0: row.so_ct0 || null,
      so_ct2: row.so_ct2 || null,
      ngay_ct0: row.ngay_hd || null,
      ma_thue: lookupUuid(row.ma_thue_data) || null,
      thue_suat: scalarValue(asRecord(row.ma_thue_data)?.thue_suat) || null,
      ma_mau_ct: row.ma_mau_ct || null,
      ma_mau_bc: row.ma_mau_bc || null,
      ma_tc_thue: row.ma_tc_thue || null,
      ma_kh: row.ma_kh_data?.uuid || row.ma_kh || null,
      ten_kh_thue: row.ma_kh_data?.customer_name || row.ten_kh_thue || null,
      dia_chi: row.ma_kh_data?.address || row.dia_chi || null,
      ma_so_thue: row.ma_kh_data?.tax_code || row.ma_so_thue || null,
      ten_vt_thue: row.ten_vt_thue || null,
      t_tien_nt: row.t_tien_nt || 0,
      tk_thue_no: row.tk_thue_no_data?.uuid || null,
      tk_du: row.tk_du_data?.uuid || null,
      t_thue_nt: row.t_thue_nt || 0,
      ma_kh9: lookupUuid(row.ma_kh9_data) || null,
      ma_tt: lookupUuid(row.ma_tt_data) || null,
      ghi_chu: row.ghi_chu || null,
      ma_bp: lookupUuid(row.ma_bp_data) || null,
      ma_vv: lookupUuid(row.ma_vv_data) || null,
      ma_hd: lookupUuid(row.ma_hd_data) || null,
      ma_dtt: lookupUuid(row.ma_dtt_data) || null,
      ma_ku: lookupUuid(row.ma_ku_data) || null,
      ma_phi: lookupUuid(row.ma_phi_data) || null,
      ma_sp: lookupUuid(row.ma_sp_data) || null,
      ma_lsx: lookupUuid(row.ma_lsx_data) || null,
      ma_cp0: row.ma_cp0_data?.uuid || null
    };
  });
};

export const transformFormData = (
  data: PhieuChiInput,
  state: FormFieldState,
  formMode: string,
  tongTien: number,
  tongThue: number,
  tongThanhToan: number,
  detailRows: PhieuChiDetailRow[] = [],
  taxRows: PhieuChiTaxRow[] = []
) => {
  const chi_tiet = transformDetailRows(detailRows, formMode, data.dien_giai || '', state.loaiPhieuChi);
  const thue = transformTaxRows(taxRows, formMode);

  return {
    ...data,

    // Document type and basic info
    ma_ngv: state.loaiPhieuChi || '1',

    // Account information from state
    tk: state.taiKhoan?.uuid || undefined,

    // Document number and series from state
    ...transformDocumentNumber(state.quyenChungTu, state.soChungTu),

    ma_tt: state.thanhToan?.uuid || undefined,

    // Total amounts
    t_tien_nt: tongTien,
    t_tien: tongTien,
    t_thue_nt: tongThue,
    t_thue: tongThue,
    t_tt_nt: tongThanhToan,
    t_tt: tongThanhToan,

    chi_tiet,
    thue
  };
};

/**
 * Transform invoice data to phieu chi initial form data
 * @param hoaDonData - Invoice data from API
 * @returns Initial form data for phieu chi
 */
export const transformDataFromHoaDon = (hoaDonData: PurchaseInvoiceSource): PhieuChiFormInitialData => {
  if (!hoaDonData) return {};

  const currentDate = new Date().toISOString().split('T')[0];
  const firstDetail = getInvoiceDetail(hoaDonData);
  const thueSuatData = firstDetail.thue_suat_data || null;

  // Create raw detail data that will be processed by transformDetailRows later
  const baseTime = Date.now();
  const chi_tiet = [
    {
      uuid: `temp-${baseTime}`,
      dien_giai: hoaDonData.dien_giai,

      ma_kh_data: hoaDonData.ma_kh_data || null,
      dia_chi: hoaDonData.dia_chi,

      tk_no_data: hoaDonData.tk_data || null,

      tien_nt: hoaDonData.t_tt,
      tien: hoaDonData.t_tt,
      ty_gia2: String(hoaDonData.ty_gia || 1),

      id_hd_data: {
        ID: hoaDonData.uuid,
        so_ct: hoaDonData.so_ct,
        ngay_ct: hoaDonData.ngay_ct,
        tk_data: hoaDonData.tk_data,
        ngoai_te: hoaDonData.ma_nt_data?.ma_nt,
        ty_gia: hoaDonData.ty_gia || 1,
        tien_tren_hd: hoaDonData.t_tt,
        tien_hd_nt: hoaDonData.t_tt,
        tien_con_phai_tt: hoaDonData.t_tt
      },

      ma_thue_data: thueSuatData,
      thue_suat: thueSuatData?.thue_suat || 0,
      thue_nt: 0,
      thue: 0,

      ma_bp_data: firstDetail.ma_bp_data || null,
      ma_vv_data: firstDetail.ma_vv_data || null,
      ma_hd_data: firstDetail.ma_hd_data || null,
      ma_dtt_data: hoaDonData.ma_dtt_data || null,
      ma_ku_data: firstDetail.ma_ku_data || null,
      ma_phi_data: hoaDonData.ma_phi_data || null,
      ma_cp0_data: firstDetail.ma_cp0_data || null
    }
  ] as unknown as PhieuChiDetailRow[];

  return {
    ma_ngv: '1',
    ngay_ct: currentDate,
    ngay_ct0: hoaDonData.ngay_ct || currentDate,
    so_ct0: hoaDonData.so_ct || '',
    dien_giai: hoaDonData.dien_giai || '',

    ma_kh: hoaDonData.ma_kh_data?.uuid || null,
    ong_ba: hoaDonData.ma_kh_data?.contact_person || hoaDonData.ong_ba || '',
    dia_chi: hoaDonData.ma_kh_data?.address || hoaDonData.dia_chi || '',

    ma_nt: hoaDonData.ma_nt || 'VND',
    ty_gia: String(hoaDonData.ty_gia || 1),

    status: '0',

    chi_tiet
  };
};

/**
 * Transform service invoice data to phieu chi initial form data
 * @param hoaDonData - Service invoice data from API
 * @returns Initial form data for phieu chi
 */
export const transformDataFromHoaDonDichVu = (hoaDonData: PurchaseInvoiceSource): PhieuChiFormInitialData => {
  if (!hoaDonData) return {};

  const currentDate = new Date().toISOString().split('T')[0];

  const baseTime = Date.now();
  const chi_tiet = (hoaDonData.chi_tiet_data || []).map((item, index: number) => {
    const detailItem = item as InvoiceDetailSource;
    const maDvData = detailItem.ma_dv_data;
    const maThueData = detailItem.ma_thue_data || getNestedLookup(maDvData, 'thue_suat_data');

    return {
      uuid: `temp-${baseTime}-${index}`,
      line: index + 1,
      dien_giai: hoaDonData.dien_giai,

      ma_kh_data: hoaDonData.ma_kh_data || null,
      dia_chi: hoaDonData.dia_chi,

      tk_no_data: detailItem.tk_dt_data || hoaDonData.tk_data || null,

      tien_nt: hoaDonData.t_tt || detailItem.tien_nt || 0,
      tien: hoaDonData.t_tt || detailItem.tien_nt || 0,
      ty_gia2: String(hoaDonData.ty_gia || 1),

      id_hd_data: {
        ID: hoaDonData.uuid,
        so_ct: hoaDonData.so_ct,
        ngay_ct: hoaDonData.ngay_ct,
        tk_data: hoaDonData.tk_data,
        ngoai_te: hoaDonData.ma_nt_data?.ma_nt,
        ty_gia: hoaDonData.ty_gia || 1,
        tien_tren_hd: hoaDonData.t_tt,
        tien_hd_nt: hoaDonData.t_tt,
        tien_con_phai_tt: hoaDonData.t_tt
      },

      ma_thue_data: maThueData || null,
      thue_suat: scalarValue(asRecord(maThueData)?.thue_suat) || 0,
      thue_nt: 0,
      thue: 0,

      ma_bp_data: detailItem.ma_bp_data || getNestedLookup(maDvData, 'ma_bp_data') || null,
      ma_vv_data: detailItem.ma_vv_data || getNestedLookup(maDvData, 'ma_vv_data') || null,
      ma_hd_data: detailItem.ma_hd_data || getNestedLookup(maDvData, 'ma_hd_data') || null,
      ma_dtt_data: detailItem.ma_dtt_data || getNestedLookup(maDvData, 'ma_dtt_data') || null,
      ma_ku_data: detailItem.ma_ku_data || getNestedLookup(maDvData, 'ma_ku_data') || null,
      ma_phi_data: detailItem.ma_phi_data || null,
      ma_cp0_data: detailItem.ma_cp0_data || null
    };
  }) as unknown as PhieuChiDetailRow[];

  return {
    ma_ngv: '1',
    ngay_ct: currentDate,
    ngay_ct0: hoaDonData.ngay_ct || currentDate,
    so_ct0: hoaDonData.so_ct || '',
    dien_giai: hoaDonData.dien_giai || '',

    ma_kh: hoaDonData.ma_kh_data?.uuid || null,
    ong_ba: hoaDonData.ma_kh_data?.contact_person || hoaDonData.ong_ba || '',
    dia_chi: hoaDonData.ma_kh_data?.address || hoaDonData.dia_chi || '',

    ma_nt: hoaDonData.ma_nt || 'VND',
    ty_gia: String(hoaDonData.ty_gia || 1),

    status: '0',

    chi_tiet
  };
};
